import React, { Dispatch, ReactElement, SetStateAction, useState } from 'react';
import * as Sentry from '@sentry/browser';
import Button from 'components/form/button/button';
import TextInput from 'components/form/text-input/text-input';
import { useMutation } from 'react-query';
import { couponsApi } from 'api/bestpark/coupons';
import Alert from 'components/alerts/alert';
import { useTranslate } from 'hooks/translate';
import { useReservations } from 'contexts/reservations-context';
import './coupon-form.scss';
import { CouponValidationDTO } from 'types/models/coupons';
import { useNotification } from 'hooks/notification';
import {
  CouponValidationResult,
  DEFAULT_VALIDATION_ERROR,
  VALIDATION_ERROR_MAP,
} from 'constants/coupons';
import { DEFAULT_NOTIFICATION_DURATION_MS } from 'constants/utils';

export interface CouponFormBody {
  code: string;
}

interface CouponProps {
  price: number;
  setValidCouponCode: Dispatch<SetStateAction<string>>;
  customer: Customer;
}

export const CouponForm = ({
  price,
  setValidCouponCode,
  customer,
}: CouponProps): ReactElement => {
  const { t } = useTranslate();
  const [code, setCode] = useState('');
  const { startDate, endDate, parkId: campId } = useReservations();
  const { notification, showNotification } = useNotification();
  const { id: customerId } = customer;
  const couponLocalizationPath = 'reservation.coupon';

  const onSuccess = (validationResult: CouponValidationResult): void => {
    const { success, remainingUsages, errorCode } = validationResult;
    let message = '';

    if (success) {
      message = t(`${couponLocalizationPath}.couponApplied`);
      if (remainingUsages?.nightsPerCustomer) {
        const { nightsPerCustomer } = remainingUsages;
        message +=
          ' ' +
          t(
            `${couponLocalizationPath}.validation.customerRemainingNights`,
            `${nightsPerCustomer}`,
          );
      }
      showNotification(message, 'success');
      setTimeout(() => {
        setValidCouponCode(code);
      }, DEFAULT_NOTIFICATION_DURATION_MS);
    } else {
      message = errorCode
        ? t(VALIDATION_ERROR_MAP[errorCode]) ?? t(DEFAULT_VALIDATION_ERROR)
        : t(DEFAULT_VALIDATION_ERROR);
      showNotification(message, 'error');
      setValidCouponCode('');
    }
  };

  const handleApiError = (error: any, messageString?: string): void => {
    showNotification(messageString ?? t('general.errors.default'), 'error');
    Sentry.withScope((scope) => {
      scope.setExtra('error', error.message);
      scope.setExtra('message', messageString ?? '');
      Sentry.captureMessage('Error in coupon form');
    });
  };

  const [validateCoupon, validateData] = useMutation(
    couponsApi.validateCoupon,
    {
      onSuccess,
      onError: (error) =>
        handleApiError(error, t(`${couponLocalizationPath}.validationFailed`)),
    },
  );

  const validationBody: CouponValidationDTO = {
    code,
    customerId,
    campId,
    price,
    startDate,
    endDate,
  };

  const submitValidation = async (): Promise<void> => {
    setValidCouponCode('');
    await validateCoupon(validationBody);
  };

  return (
    <div className='coupon-container'>
      {notification.text && (
        <div className='notification'>
          <Alert
            message={notification.text}
            variant={notification.type === 'success' ? 'success' : ''}
          />{' '}
          <br />
        </div>
      )}
      <div className='coupon-content'>
        <div>
          <TextInput
            id='code'
            label={t(`${couponLocalizationPath}.couponCode`)}
            name='code'
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
        </div>
        <Button
          className='primary'
          action={submitValidation}
          disabled={validateData.isLoading}
        >
          {t(`${couponLocalizationPath}.validateCoupon`)}
        </Button>
      </div>
    </div>
  );
};
